/**
 * The account menu's words — pure, so they unit-test without Firebase or the
 * build constants (the env-gate.ts / late.ts split).
 *
 * Ed, 2026-09-24: the planner gets the top-right account menu the carer app
 * already has (OMON-322, grown by OMON-349). Same header, same order, same
 * labels, so a person who uses both apps finds their account in one place:
 * who you are (name, email, role · environment), then Change password,
 * Support, Sign out, and the app version.
 */
import type { AppEnv } from "./env-gate";
import { roleName } from "./admin/access-labels";

export interface AccountFacts {
  name: string | null;
  email: string | null;
  role: string | null;
  env: AppEnv;
  version: string;
}

export interface AccountSummary {
  /** First line of the header. Falls back to the email, never invents. */
  title: string;
  /** Second line: the email, or null when it already is the title. */
  email: string | null;
  /** "Admin · production". An absent role says so (the carer app's rule). */
  meta: string;
}

// Always lower case (Ed, 2026-09-25), in every app and surface: the
// carer app, Circle of Support and the planner all say "production".
const ENV_LABEL: Record<AppEnv, string> = {
  prod: "production",
  develop: "develop",
  demo: "demo",
  test: "test",
  training: "training",
  nonprod: "non-production",
};

export function envLabel(env: AppEnv): string {
  return ENV_LABEL[env];
}

export function accountSummary(f: AccountFacts): AccountSummary {
  const title = f.name || f.email || "Signed in";
  return {
    title,
    email: f.email && f.email !== title ? f.email : null,
    meta: `${f.role ? roleName(f.role) : "No role assigned"} · ${envLabel(f.env)}`,
  };
}

/** Where Support goes (OMON-349): support@ is the support inbox (Zammad
 *  today) and stays the address whatever runs behind it (Ed, 2026-09-25). */
export const SUPPORT_EMAIL = "support@sweettreeconnect.com";

/** A support email that already carries what support asks for first:
 *  who, which role, which environment, which version. Outside production
 *  the subject says which environment, so a training or demo question is
 *  never mistaken for a production problem. */
export function supportHref(f: AccountFacts): string {
  const where = f.env === "prod" ? "" : ` [${envLabel(f.env)}]`;
  const subject = `Planner support${where}`;
  const body = [
    "What happened, and what did you expect?",
    "",
    "",
    "---",
    `Account: ${f.email ?? "unknown"}`,
    `Role: ${f.role ? roleName(f.role) : "No role assigned"}`,
    `Environment: ${envLabel(f.env)}`,
    `Planner version: ${f.version}`,
  ].join("\n");
  return `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}
